/**
 * Windows-95 push button — a silver raised-bevel chip with near-black bold
 * label, used by the asset file picker's footer (Take Photo / Cancel / Select)
 * and its title-bar close box. Pointer-clickable; paints a focus state on
 * hover (and exposes `setFocused` so a controller focus ring could drive it).
 * The optional `defaultButton` flag draws the extra black outer ring Win95
 * gives the dialog's default action.
 *
 * Position (`x`, `y`) is the button's centre, matching `PauseMenuButton`.
 */

import Phaser from "phaser";

import { PALETTE } from "../config";
import { drawRaisedBevel } from "./win95";

const HEIGHT = 16;

/** Optional leading glyph: drawn into the button's Graphics at (cx, cy), with
 *  `width` px reserved to the left of the label so the two stay group-centred. */
export interface Win95ButtonIcon {
  width: number;
  draw: (g: Phaser.GameObjects.Graphics, cx: number, cy: number) => void;
}

export class Win95Button {
  readonly container: Phaser.GameObjects.Container;
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly w: number;
  private readonly h: number;
  private readonly defaultButton: boolean;
  private readonly icon?: Win95ButtonIcon;
  private iconCx = 0;
  private focused = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    labelText: string,
    onActivate: () => void,
    width = 76,
    height = HEIGHT,
    defaultButton = false,
    icon?: Win95ButtonIcon,
  ) {
    this.w = width;
    this.h = height;
    this.defaultButton = defaultButton;
    this.icon = icon;
    this.container = scene.add.container(x, y).setDepth(46);

    this.g = scene.add.graphics();

    const label = scene.add
      .text(0, 0, labelText, {
        fontFamily: "monospace",
        fontSize: `${Math.max(7, Math.min(9, height - 7))}px`,
        color: `#${PALETTE.win95Text.toString(16).padStart(6, "0")}`,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    // Offset label right to make room for a leading icon, keeping the
    // icon + label pair centred within the button.
    if (icon) {
      const gap = 3;
      const total = icon.width + gap + label.width;
      this.iconCx = -total / 2 + icon.width / 2;
      label.setX(-total / 2 + icon.width + gap + label.width / 2);
    }

    // Transparent-but-interactive hit zone on top, so the whole chip clicks.
    const hit = scene.add
      .rectangle(0, 0, width, height, 0x000000, 0.001)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    hit.on("pointerdown", onActivate);
    hit.on("pointerover", () => this.setFocused(true));
    hit.on("pointerout", () => this.setFocused(false));

    this.container.add([this.g, label, hit]);
    this.paint();
  }

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    this.paint();
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  destroy(): void {
    this.container.destroy();
  }

  static readonly HEIGHT = HEIGHT;

  private paint(): void {
    const x = -this.w / 2;
    const y = -this.h / 2;
    this.g.clear();
    // Default action gets the extra 1px black ring; reserve room for it.
    if (this.defaultButton) {
      this.g.lineStyle(1, PALETTE.win95DarkShadow, 1).strokeRect(x, y, this.w, this.h);
      drawRaisedBevel(this.g, x + 1, y + 1, this.w - 2, this.h - 2);
    } else {
      drawRaisedBevel(this.g, x, y, this.w, this.h);
    }
    // Keyboard/hover focus marquee: a dotted-style inset rectangle.
    if (this.focused) {
      const inset = this.defaultButton ? 4 : 3;
      this.g.lineStyle(1, PALETTE.win95DarkShadow, 0.9);
      this.g.strokeRect(x + inset, y + inset, this.w - inset * 2, this.h - inset * 2);
    }
    // Leading icon, drawn last so it sits on top of the bevelled face.
    this.icon?.draw(this.g, this.iconCx, 0);
  }
}
